import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiDatabase as DatabaseIcon,
  FiLayers as LayersIcon,
  FiClock as ClockIcon,
  FiTag as TagIcon,
  FiSearch as SearchIcon,
  FiFilter as FilterIcon,
  FiX as XIcon,
  FiCheckCircle as CheckCircleIcon,
  FiXCircle as XCircleIcon,
  FiLoader as LoaderIcon,
  FiRefreshCw as RefreshIcon,
  FiAlertTriangle as AlertTriangleIcon,
  FiTrash2 as TrashIcon,
  FiSquare as Square,
  FiCheckSquare as CheckSquare,
  FiChevronRight as ChevronRight,
} from 'react-icons/fi';
import { catalogAPI } from '../../../services/dataopsApi';
import { bulkDeleteDatasets, bulkDeleteImportJobs } from '../../../services/aiOperationsApi';
import { markDataOpsStepComplete } from '../../../hooks/useDataOpsOnboardingStatus';
import ProjectAssignmentSelect from '../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../components/WorkloadAssignmentSelect';
import { useProject } from '../../../contexts/ProjectContext';
import { useWorkloads } from '../../hub/workloads/hooks/useWorkloads';
import { useFilter } from '../../../contexts/FilterContext';

interface CatalogOverview {
  total_datasets: number;
  total_jobs: number;
  active_jobs: number;
  namespaces_count: number;
}

interface Dataset {
  id: string;
  name: string;
  namespace?: string;
  source?: string;
  description?: string;
  quality_score: number;
  last_updated?: string | null;
  tags?: string[];
  project_id?: string | null;
  workload_id?: string | null;
  [key: string]: unknown;
}

interface Job {
  id: string;
  name: string;
  namespace?: string;
  status?: string;
  description?: string;
  last_run_at?: string | null;
  tags?: string[];
  cloud_service?: string;
  cloud_metadata?: Record<string, unknown>;
  code_location?: string;
  runtime_version?: string;
  project_id?: string | null;
  workload_id?: string | null;
  [key: string]: unknown;
}

interface Namespace {
  id: string;
  name: string;
}

interface RuleResult {
  description?: string;
  name?: string;
  result: string;
  evaluation_message?: string;
}

interface QualityResult {
  score: number;
  rules_passed: number;
  rules_failed: number;
  rules_total: number;
  completed_at?: string | null;
  rule_results?: RuleResult[];
}

interface GlueRoleError {
  error_code?: string;
  role_name?: string;
  message?: string;
  account_id?: string;
}

interface ApiError extends Error {
  data?: GlueRoleError;
}

type ViewMode = 'dataset' | 'job';
type ActiveTab = 'datasets' | 'jobs';

function getQualityColor(score: number): string {
  if (score >= 95) return 'text-green-600 dark:text-green-400';
  if (score >= 85) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'running':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
    case 'success':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    case 'failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    case 'pending':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

function friendlyRuleDescription(description?: string): string {
  if (!description) return description ?? '';
  const d = description.trim();
  let m: RegExpMatchArray | null;
  if ((m = d.match(/^IsComplete\s+"(.+)"$/))) return `Column "${m[1]}" has no null values`;
  if ((m = d.match(/^IsUnique\s+"(.+)"$/))) return `Column "${m[1]}" has all unique values`;
  if (d === 'RowCount > 0') return 'Table has at least one row';
  if ((m = d.match(/^ColumnValues\s+"(.+)"\s*>=\s*0$/))) return `Column "${m[1]}" has no negative values`;
  if ((m = d.match(/^DataFreshness\s+"(.+)"\s*<=\s*(\d+)\s*days$/)))
    return `Column "${m[1]}" data is within ${m[2]} days`;
  return d;
}

const getProjectName = (projectId: string | null | undefined, projectList: { id: string; name: string }[]): string | null => {
  if (!projectId) return null;
  return projectList.find(p => p.id === projectId)?.name ?? null;
};

const getWorkloadName = (workloadId: string | null | undefined, workloadList: { id: string; name: string }[]): string | null => {
  if (!workloadId) return null;
  return workloadList.find(w => w.id === workloadId)?.name ?? null;
};

const CatalogPage: React.FC = () => {
  const { selectedProjectId, projects } = useProject();
  const { workloads } = useWorkloads();
  const { filterType, selectedId: filterId } = useFilter();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<CatalogOverview | null>(null);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [namespaces, setNamespaces] = useState<Namespace[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>('datasets');
  const [searchQuery, setSearchQuery] = useState('');
  const [namespaceFilter, setNamespaceFilter] = useState('All Namespaces');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('dataset');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Dataset | Job | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Data Quality state
  const [dqRunning, setDqRunning] = useState<Record<string, boolean>>({});
  const [dqResults, setDqResults] = useState<QualityResult[] | null>(null);
  const [dqResultsLoading, setDqResultsLoading] = useState(false);
  const dqPollingRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const [glueRoleError, setGlueRoleError] = useState<GlueRoleError | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Mark onboarding step as complete when visiting this page
  useEffect(() => {
    markDataOpsStepComplete('explore_catalog');
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, filterType, filterId]);

  const loadQualityResults = useCallback(async (datasetId: string) => {
    setDqResultsLoading(true);
    setDqResults(null);
    try {
      const results: QualityResult[] = await catalogAPI.getQualityResults(datasetId);
      setDqResults(results);
    } catch (err: unknown) {
      console.error('Error loading quality results:', err instanceof Error ? err.message : err);
    } finally {
      setDqResultsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!viewModalOpen || !selectedId) return;
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    setDqResults(null);
    const fetchDetail =
      viewMode === 'dataset'
        ? () => catalogAPI.getDataset(selectedId)
        : () => catalogAPI.getJob(selectedId);
    fetchDetail()
      .then((d: Dataset | Job) => setDetail(d))
      .catch((err: unknown) =>
        setDetailError(err instanceof Error ? err.message : 'Failed to load details')
      )
      .finally(() => setDetailLoading(false));
    if (viewMode === 'dataset') {
      loadQualityResults(selectedId);
    }
  }, [viewModalOpen, viewMode, selectedId, loadQualityResults]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewData, datasetsData, jobsData, namespacesData] = await Promise.all([
        catalogAPI.getOverview(),
        catalogAPI.getDatasets(),
        catalogAPI.getJobs(),
        catalogAPI.getNamespaces(),
      ]);
      setOverview(overviewData);
      setDatasets(datasetsData);
      setJobs(jobsData);
      setNamespaces(namespacesData);
    } catch (err: unknown) {
      console.error('Error loading catalog data:', err instanceof Error ? err.message : err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDatasets = datasets.filter((dataset) => {
    const matchesSearch =
      !searchQuery ||
      dataset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dataset.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesNamespace =
      namespaceFilter === 'All Namespaces' || dataset.namespace === namespaceFilter;
    return matchesSearch && matchesNamespace;
  });

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      !searchQuery ||
      job.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesNamespace =
      namespaceFilter === 'All Namespaces' || job.namespace === namespaceFilter;
    return matchesSearch && matchesNamespace;
  });

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = () => {
    const items = activeTab === 'datasets' ? filteredDatasets : filteredJobs;
    if (selectedIds.size === items.length && items.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((d) => d.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const label = activeTab === 'datasets' ? 'dataset(s)' : 'job(s)';
    if (!globalThis.confirm(`Delete ${selectedIds.size} ${label}? This cannot be undone.`)) return;
    try {
      if (activeTab === 'datasets') {
        await bulkDeleteDatasets(Array.from(selectedIds));
      } else {
        await bulkDeleteImportJobs(Array.from(selectedIds));
      }
      setSelectedIds(new Set());
      loadData();
    } catch (err: unknown) {
      console.error('Error deleting:', err instanceof Error ? err.message : err);
    }
  };

  // Clean up polling on unmount
  useEffect(() => {
    const pollingRef = dqPollingRef.current;
    return () => {
      Object.values(pollingRef).forEach(clearInterval);
    };
  }, []);

  const handleRunQualityCheck = useCallback(async (datasetId: string) => {
    try {
      setDqRunning((prev) => ({ ...prev, [datasetId]: true }));
      const environmentId = localStorage.getItem('selectedEnvironmentId') || '';
      await catalogAPI.runDataQuality(datasetId, environmentId);

      const pollInterval = setInterval(async () => {
        try {
          const statusResp = await catalogAPI.getQualityStatus(datasetId);
          if (['completed', 'failed'].includes(statusResp.status)) {
            clearInterval(pollInterval);
            delete dqPollingRef.current[datasetId];
            setDqRunning((prev) => ({ ...prev, [datasetId]: false }));
            if (statusResp.status === 'completed') {
              loadData();
            }
          }
        } catch {
          // Ignore polling errors
        }
      }, 10000);
      dqPollingRef.current[datasetId] = pollInterval;
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.data?.error_code === 'GLUE_ROLE_NOT_FOUND') {
        setGlueRoleError(apiErr.data);
      } else {
        console.error('Error triggering quality check:', err instanceof Error ? err.message : err);
      }
      setDqRunning((prev) => ({ ...prev, [datasetId]: false }));
    }
  }, []);

  const openViewDataset = (id: string) => {
    setViewMode('dataset');
    setSelectedId(id);
    setViewModalOpen(true);
  };

  const openViewJob = (id: string) => {
    setViewMode('job');
    setSelectedId(id);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedId(null);
    setDetail(null);
    setDetailError(null);
    setDqResults(null);
  };

  const renderGlueJobSections = (job: Job) => {
    const meta = job.cloud_metadata ?? {};
    const cmd = (meta.Command ?? {}) as Record<string, unknown>;
    const defaultArgs = meta.DefaultArguments as Record<string, unknown> | undefined;
    const codegenNodes = meta.CodeGenConfigurationNodes as Record<string, unknown> | undefined;

    const scriptValue = job.code_location || (cmd.ScriptLocation as string | undefined);
    const glueVersion = job.runtime_version || (meta.GlueVersion as string | undefined);
    const workerType = meta.WorkerType as string | undefined;
    const numberOfWorkers = meta.NumberOfWorkers as number | null | undefined;
    const executionClass = meta.ExecutionClass as string | undefined;
    const timeout = meta.Timeout as number | null | undefined;

    return (
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">AWS Glue job</h4>
        <div>
          <dt className="font-medium text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider mb-1">Runtime</dt>
          <dd className="space-y-1 text-sm text-gray-900 dark:text-white">
            {scriptValue && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Script:</span>{' '}
                {String(scriptValue)}
              </div>
            )}
            {glueVersion && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Glue version:</span>{' '}
                {String(glueVersion)}
              </div>
            )}
            {workerType && (
              <div><span className="text-gray-500 dark:text-gray-400">Worker type:</span> {workerType}</div>
            )}
            {numberOfWorkers != null && (
              <div><span className="text-gray-500 dark:text-gray-400">Workers:</span> {numberOfWorkers}</div>
            )}
            {executionClass && (
              <div><span className="text-gray-500 dark:text-gray-400">Execution class:</span> {executionClass}</div>
            )}
            {timeout != null && (
              <div><span className="text-gray-500 dark:text-gray-400">Timeout:</span> {timeout} min</div>
            )}
          </dd>
        </div>
        {defaultArgs && Object.keys(defaultArgs).length > 0 && (
          <div>
            <dt className="font-medium text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider mb-2">Default arguments</dt>
            <dd className="rounded-md bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="min-w-full text-xs">
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {Object.entries(defaultArgs).map(([key, value]) => (
                    <tr key={`arg-${key}`}>
                      <td className="py-1.5 px-3 font-medium text-gray-600 dark:text-gray-400 align-top whitespace-nowrap w-1/3">{key}</td>
                      <td className="py-1.5 px-3 text-gray-900 dark:text-white break-all">{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </dd>
          </div>
        )}
        {codegenNodes && Object.keys(codegenNodes).length > 0 && (
          <div>
            <dt className="font-medium text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider mb-2">Pipeline nodes</dt>
            <dd className="space-y-3">
              {Object.entries(codegenNodes).map(([nodeId, nodeConfig]) => {
                const nodeConfigObj = nodeConfig as Record<string, unknown>;
                const typeKey = Object.keys(nodeConfigObj).find(
                  (k) => typeof nodeConfigObj[k] === 'object' && nodeConfigObj[k] !== null
                );
                const node = (typeKey ? nodeConfigObj[typeKey] : {}) as Record<string, unknown>;
                const typeLabel = typeKey ? typeKey.replaceAll(/([A-Z])/g, ' $1').trim() : 'Node';
                const paths = node.Paths as string[] | undefined;
                const inputs = node.Inputs as string[] | undefined;
                const filters = node.Filters as unknown[] | undefined;
                const outputSchemas = node.OutputSchemas as Array<{ Columns?: Array<{ Name: string }> }> | undefined;
                const nodeName = node.Name as string | undefined;
                const nodePath = node.Path as string | undefined;
                const nodeScript = node.ScriptLocation as string | undefined;
                const nodeCompression = node.Compression as string | undefined;
                return (
                  <div key={`node-${nodeId}`} className="rounded-md bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-3 text-xs">
                    <div className="font-medium text-gray-700 dark:text-gray-300 mb-1.5">{nodeId}</div>
                    <div className="text-gray-500 dark:text-gray-400 mb-1">{typeLabel}</div>
                    <div className="space-y-1 text-gray-900 dark:text-white">
                      {nodeName && <div><span className="text-gray-500 dark:text-gray-400">Name:</span> {nodeName}</div>}
                      {paths && Array.isArray(paths) && <div><span className="text-gray-500 dark:text-gray-400">Paths:</span> {paths.join(', ')}</div>}
                      {nodePath && <div><span className="text-gray-500 dark:text-gray-400">Path:</span> {nodePath}</div>}
                      {inputs && Array.isArray(inputs) && <div><span className="text-gray-500 dark:text-gray-400">Inputs:</span> {inputs.join(', ')}</div>}
                      {nodeScript && <div><span className="text-gray-500 dark:text-gray-400">Script:</span> {nodeScript}</div>}
                      {nodeCompression && <div><span className="text-gray-500 dark:text-gray-400">Compression:</span> {nodeCompression}</div>}
                      {filters && Array.isArray(filters) && (
                        <div><span className="text-gray-500 dark:text-gray-400">Filters:</span> {filters.length} filter(s)</div>
                      )}
                      {outputSchemas && Array.isArray(outputSchemas) && outputSchemas[0]?.Columns && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Columns:</span>{' '}
                          {outputSchemas[0].Columns.map((c) => c.Name).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </dd>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  const currentItems = activeTab === 'datasets' ? filteredDatasets : filteredJobs;
  const allSelected = selectedIds.size === currentItems.length && currentItems.length > 0;

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <DatabaseIcon className="w-12 h-12 text-blue-600 dark:text-blue-400 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Data Catalog</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">Browse datasets, jobs, and data lineage</p>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <DatabaseIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Datasets</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{overview?.total_datasets || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <LayersIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{overview?.total_jobs || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <ClockIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Jobs</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{overview?.active_jobs || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                <TagIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Namespaces</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{overview?.namespaces_count || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={toggleSelectAll}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label={allSelected ? 'Deselect all' : 'Select all'}
            >
              {allSelected ? (
                <CheckSquare className="w-5 h-5 text-blue-600" />
              ) : (
                <Square className="w-5 h-5 text-gray-400" />
              )}
            </button>
            <div className="flex items-center gap-2">
              <FilterIcon className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={activeTab === 'datasets' ? 'Search datasets...' : 'Search jobs...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <select
              value={namespaceFilter}
              onChange={(e) => setNamespaceFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option>All Namespaces</option>
              {namespaces.map((ns) => (
                <option key={ns.id} value={ns.name}>{ns.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={loadData}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <RefreshIcon className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">{selectedIds.size} selected</span>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              currentProjectId={null}
              onAssign={async (projectId) => {
                const entityType = activeTab === 'datasets' ? 'dataset' as const : 'job' as const;
                await catalogAPI.bulkAssignProject(Array.from(selectedIds), entityType, projectId);
                if (entityType === 'dataset') {
                  setDatasets(prev => prev.map(d => selectedIds.has(d.id) ? { ...d, project_id: projectId } : d));
                } else {
                  setJobs(prev => prev.map(j => selectedIds.has(j.id) ? { ...j, project_id: projectId } : j));
                }
                setSelectedIds(new Set());
              }}
              size="sm"
            />
            <WorkloadAssignmentSelect
              bulk
              currentWorkloadId={null}
              onAssign={async (workloadId) => {
                const entityType = activeTab === 'datasets' ? 'dataset' as const : 'job' as const;
                await catalogAPI.bulkAssignWorkload(Array.from(selectedIds), entityType, workloadId);
                setSelectedIds(new Set());
                loadData();
              }}
              size="sm"
            />
            <button
              type="button"
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Glue Role Missing Banner */}
      {glueRoleError && (
        <div className="mb-6 rounded-lg border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-start">
            <AlertTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                IAM Role Missing: {glueRoleError.role_name}
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                {glueRoleError.message}
              </p>
              <div className="mt-3 text-sm text-amber-700 dark:text-amber-400">
                <p className="font-medium mb-2">To create this role in AWS account {glueRoleError.account_id}:</p>
                <ol className="list-decimal list-inside space-y-1 ml-1">
                  <li>Go to IAM &gt; Roles &gt; Create Role in the AWS Console</li>
                  <li>Select <strong>AWS service</strong> as trusted entity, then choose <strong>Glue</strong></li>
                  <li>Attach the managed policy <code className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded text-xs">AWSGlueServiceRole</code></li>
                  <li>Add an inline policy granting <code className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded text-xs">s3:GetObject</code> and <code className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded text-xs">s3:ListBucket</code> on the S3 buckets where your Glue catalog tables store data</li>
                  <li>Name the role exactly: <code className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded text-xs font-semibold">{glueRoleError.role_name}</code></li>
                </ol>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setGlueRoleError(null)}
              className="ml-3 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-8">
          <button
            type="button"
            onClick={() => { setActiveTab('datasets'); setSelectedIds(new Set()); }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'datasets'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            Datasets ({filteredDatasets.length})
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('jobs'); setSelectedIds(new Set()); }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'jobs'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            Jobs ({filteredJobs.length})
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'datasets' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
          {filteredDatasets.map((dataset) => {
            let qualityDisplay: React.ReactElement | null = null;
            if (dqRunning[dataset.id]) {
              qualityDisplay = (
                <div className="flex items-center text-xs text-blue-600 dark:text-blue-400">
                  <LoaderIcon className="w-3.5 h-3.5 mr-1 animate-spin" />
                  Running...
                </div>
              );
            } else if (dataset.quality_score > 0) {
              qualityDisplay = (
                <div className="flex items-center gap-1.5">
                  <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${getQualityColor(dataset.quality_score).replace('text-', 'bg-')}`}
                      style={{ width: `${dataset.quality_score}%` }}
                    />
                  </div>
                  <span className={`text-xs font-medium ${getQualityColor(dataset.quality_score)}`}>
                    {dataset.quality_score}%
                  </span>
                </div>
              );
            } else {
              qualityDisplay = (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRunQualityCheck(dataset.id);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:border-blue-700 dark:hover:bg-blue-900/40"
                >
                  <RefreshIcon className="w-3 h-3" />
                  Run check
                </button>
              );
            }
            return (
            <div
              key={dataset.id}
              onClick={() => openViewDataset(dataset.id)} onKeyDown={(e) => { if (['Enter', ' '].includes(e.key)) openViewDataset(dataset.id); }} role="button" tabIndex={0}
              className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (['Enter', ' '].includes(e.key)) e.stopPropagation(); }} role="presentation">
                    <button
                      type="button"
                      onClick={() => toggleSelect(dataset.id)}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {selectedIds.has(dataset.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-white">{dataset.name}</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                        {dataset.namespace}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        {dataset.source}
                      </span>
                      {getProjectName(dataset.project_id, projects) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          {getProjectName(dataset.project_id, projects)}
                        </span>
                      )}
                      {getWorkloadName(dataset.workload_id, workloads) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                          {getWorkloadName(dataset.workload_id, workloads)}
                        </span>
                      )}
                      {qualityDisplay}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {dataset.description && <span className="truncate">{dataset.description}</span>}
                      {(dataset.tags?.length ?? 0) > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {dataset.tags!.map((tag) => (
                            <span key={`ds-tag-${dataset.id}-${tag}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
              </div>
            </div>
          );})}
        </div>
      )}

      {activeTab === 'jobs' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
          {filteredJobs.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
              No jobs found
            </div>
          ) : (
            filteredJobs.map((job) => (
              <div
                key={job.id}
                onClick={() => openViewJob(job.id)} onKeyDown={(e) => { if (['Enter', ' '].includes(e.key)) openViewJob(job.id); }} role="button" tabIndex={0}
                className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (['Enter', ' '].includes(e.key)) e.stopPropagation(); }} role="presentation">
                      <button
                        type="button"
                        onClick={() => toggleSelect(job.id)}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {selectedIds.has(job.id) ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-medium text-gray-900 dark:text-white">{job.name}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                          {job.namespace}
                        </span>
                        {job.status && (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                            {job.status}
                          </span>
                        )}
                        {getProjectName(job.project_id, projects) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                            {getProjectName(job.project_id, projects)}
                          </span>
                        )}
                        {getWorkloadName(job.workload_id, workloads) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                            {getWorkloadName(job.workload_id, workloads)}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                          <ClockIcon className="w-3 h-3" />
                          {job.last_run_at ? new Date(job.last_run_at).toLocaleDateString() : 'Never run'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {job.description && <span className="truncate">{job.description}</span>}
                        {(job.tags?.length ?? 0) > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {job.tags!.map((tag) => (
                              <span key={`job-tag-${job.id}-${tag}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* View detail modal */}
      {viewModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"
              onClick={closeViewModal}
              onKeyDown={(e) => e.key === 'Escape' && closeViewModal()}
              role="button"
              tabIndex={0}
              aria-label="Close modal"
            />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {viewMode === 'dataset' ? 'Dataset details' : 'Job details'}
                  </h3>
                  <button
                    type="button"
                    onClick={closeViewModal}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                {detailLoading && (
                  <div className="py-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>
                )}
                {detailError && (
                  <div className="py-4 text-red-600 dark:text-red-400 text-sm">{detailError}</div>
                )}
                {!detailLoading && !detailError && detail && viewMode === 'dataset' && (() => {
                  const ds = detail as Dataset;
                  return (
                    <div className="space-y-6">
                      <dl className="space-y-3 text-sm">
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Name</dt>
                          <dd className="mt-0.5 text-gray-900 dark:text-white">{ds.name}</dd>
                        </div>
                        {ds.description && (
                          <div>
                            <dt className="font-medium text-gray-500 dark:text-gray-400">Description</dt>
                            <dd className="mt-0.5 text-gray-900 dark:text-white">{ds.description}</dd>
                          </div>
                        )}
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Namespace</dt>
                          <dd className="mt-0.5 text-gray-900 dark:text-white">{ds.namespace}</dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Source</dt>
                          <dd className="mt-0.5 text-gray-900 dark:text-white">{ds.source}</dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Fields</dt>
                          <dd className="mt-0.5 text-gray-900 dark:text-white">{String(ds.fields_count ?? '')}</dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Quality score</dt>
                          <dd className="mt-0.5 text-gray-900 dark:text-white">{ds.quality_score}%</dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Last updated</dt>
                          <dd className="mt-0.5 text-gray-900 dark:text-white">
                            {ds.last_updated ? new Date(ds.last_updated).toLocaleString() : '-'}
                          </dd>
                        </div>
                        {(ds.tags?.length ?? 0) > 0 && (
                          <div>
                            <dt className="font-medium text-gray-500 dark:text-gray-400">Tags</dt>
                            <dd className="mt-1 flex flex-wrap gap-1">
                              {ds.tags!.map((tag) => (
                                <span key={`detail-tag-${tag}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                  {tag}
                                </span>
                              ))}
                            </dd>
                          </div>
                        )}
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Project</dt>
                          <dd className="mt-1">
                            <ProjectAssignmentSelect
                              currentProjectId={ds.project_id}
                              onAssign={async (projectId) => {
                                await catalogAPI.assignDatasetProject(ds.id, projectId);
                                setDetail({ ...ds, project_id: projectId });
                                const updated = datasets.map(d => d.id === ds.id ? { ...d, project_id: projectId } : d);
                                setDatasets(updated);
                              }}
                            />
                          </dd>
                        </div>
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Workload</dt>
                          <dd className="mt-1">
                            <WorkloadAssignmentSelect
                              currentWorkloadId={ds.workload_id as string | null | undefined}
                              onAssign={async (workloadId) => {
                                await catalogAPI.assignDatasetWorkload(ds.id, workloadId);
                                setDetail({ ...ds, workload_id: workloadId });
                                const updated = datasets.map(d => d.id === ds.id ? { ...d, workload_id: workloadId } : d);
                                setDatasets(updated);
                              }}
                            />
                          </dd>
                        </div>
                      </dl>

                      {/* Data Quality Section */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Data Quality</h4>
                          <button
                            type="button"
                            onClick={() => handleRunQualityCheck(ds.id)}
                            disabled={dqRunning[ds.id]}
                            className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium disabled:opacity-50"
                          >
                            <RefreshIcon className={`w-3.5 h-3.5 mr-1 ${dqRunning[ds.id] ? 'animate-spin' : ''}`} />
                            {dqRunning[ds.id] ? 'Running...' : 'Re-run'}
                          </button>
                        </div>

                        {dqResultsLoading && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">Loading quality results...</div>
                        )}

                        {!dqResultsLoading && (dqResults?.length ?? 0) > 0 && (() => {
                          const latest = dqResults![0];
                          return (
                            <div className="space-y-3">
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500 dark:text-gray-400">Score</span>
                                  <span className={`font-semibold ${getQualityColor(latest.score)}`}>
                                    {latest.score.toFixed(1)}%
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500 dark:text-gray-400">Rules</span>
                                  <span className="text-gray-900 dark:text-white">
                                    <span className="text-green-600 dark:text-green-400">{latest.rules_passed} passed</span>
                                    {latest.rules_failed > 0 && (
                                      <span className="text-red-600 dark:text-red-400 ml-2">{latest.rules_failed} failed</span>
                                    )}
                                    <span className="text-gray-400 ml-2">/ {latest.rules_total} total</span>
                                  </span>
                                </div>
                                {latest.completed_at && (
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 dark:text-gray-400">Last check</span>
                                    <span className="text-gray-900 dark:text-white">
                                      {new Date(latest.completed_at).toLocaleString()}
                                    </span>
                                  </div>
                                )}

                                {(latest.rule_results?.length ?? 0) > 0 && (
                                  <div className="mt-3">
                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Rule Results</div>
                                    <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">{latest.rule_results!.length} rules auto-configured based on table schema</div>
                                    <div className="rounded-md bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 overflow-hidden">
                                      <table className="min-w-full text-xs">
                                        <thead>
                                          <tr className="bg-gray-100 dark:bg-gray-800">
                                            <th className="py-1.5 px-3 text-left font-medium text-gray-500 dark:text-gray-400">Rule</th>
                                            <th className="py-1.5 px-3 text-left font-medium text-gray-500 dark:text-gray-400">Result</th>
                                            <th className="py-1.5 px-3 text-left font-medium text-gray-500 dark:text-gray-400">Detail</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                          {latest.rule_results!.map((rule, idx) => (
                                            <tr key={`rule-${idx}-${rule.name ?? rule.description ?? idx}`}>
                                              <td className="py-1.5 px-3">
                                                <div className="text-gray-900 dark:text-white">{friendlyRuleDescription(rule.description || rule.name)}</div>
                                                <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{rule.description || rule.name}</div>
                                              </td>
                                              <td className="py-1.5 px-3">
                                                {rule.result === 'PASS' ? (
                                                  <span className="inline-flex items-center text-green-600 dark:text-green-400">
                                                    <CheckCircleIcon className="w-3.5 h-3.5 mr-1" /> Pass
                                                  </span>
                                                ) : (
                                                  <span className="inline-flex items-center text-red-600 dark:text-red-400">
                                                    <XCircleIcon className="w-3.5 h-3.5 mr-1" /> Fail
                                                  </span>
                                                )}
                                              </td>
                                              <td className="py-1.5 px-3 text-gray-500 dark:text-gray-400">{rule.evaluation_message}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {!dqResultsLoading && (!dqResults || dqResults.length === 0) && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            No quality checks have been run yet. Click &ldquo;Re-run&rdquo; to start one.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {!detailLoading && !detailError && detail && viewMode === 'job' && (() => {
                  const job = detail as Job;
                  return (
                    <dl className="space-y-3 text-sm">
                      <div>
                        <dt className="font-medium text-gray-500 dark:text-gray-400">Name</dt>
                        <dd className="mt-0.5 text-gray-900 dark:text-white">{job.name}</dd>
                      </div>
                      {job.description && (
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Description</dt>
                          <dd className="mt-0.5 text-gray-900 dark:text-white">{job.description}</dd>
                        </div>
                      )}
                      <div>
                        <dt className="font-medium text-gray-500 dark:text-gray-400">Namespace</dt>
                        <dd className="mt-0.5 text-gray-900 dark:text-white">{job.namespace}</dd>
                      </div>
                      {job.status && (
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Status</dt>
                          <dd className="mt-0.5">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                              {job.status}
                            </span>
                          </dd>
                        </div>
                      )}
                      <div>
                        <dt className="font-medium text-gray-500 dark:text-gray-400">Last run</dt>
                        <dd className="mt-0.5 text-gray-900 dark:text-white">
                          {job.last_run_at ? new Date(job.last_run_at).toLocaleString() : 'Never'}
                        </dd>
                      </div>
                      {(job.tags?.length ?? 0) > 0 && (
                        <div>
                          <dt className="font-medium text-gray-500 dark:text-gray-400">Tags</dt>
                          <dd className="mt-1 flex flex-wrap gap-1">
                            {job.tags!.map((tag) => (
                              <span key={`job-detail-tag-${tag}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                {tag}
                              </span>
                            ))}
                          </dd>
                        </div>
                      )}
                      <div>
                        <dt className="font-medium text-gray-500 dark:text-gray-400">Project</dt>
                        <dd className="mt-1">
                          <ProjectAssignmentSelect
                            currentProjectId={job.project_id}
                            onAssign={async (projectId) => {
                              await catalogAPI.assignJobProject(job.id, projectId);
                              setDetail({ ...job, project_id: projectId });
                              const updated = jobs.map(j => j.id === job.id ? { ...j, project_id: projectId } : j);
                              setJobs(updated);
                            }}
                          />
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500 dark:text-gray-400">Workload</dt>
                        <dd className="mt-1">
                          <WorkloadAssignmentSelect
                            currentWorkloadId={job.workload_id as string | null | undefined}
                            onAssign={async (workloadId) => {
                              await catalogAPI.assignJobWorkload(job.id, workloadId);
                              setDetail({ ...job, workload_id: workloadId });
                              const updated = jobs.map(j => j.id === job.id ? { ...j, workload_id: workloadId } : j);
                              setJobs(updated);
                            }}
                          />
                        </dd>
                      </div>
                      {job.cloud_service === 'glue' && renderGlueJobSections(job)}
                    </dl>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogPage;
